// Load the logic from its versioned asset URL (passed by the section): a plain
// './commission-logic.js' import has no version, so browsers keep a stale copy
// for up to a year after an update (Shopify serves assets with max-age 1 year).
const logicUrl = document.querySelector('commission-configurator[data-logic-url]')?.dataset.logicUrl;
const {
  parseInches,
  measurementWarning,
  firstUnconfirmed,
  activeMeasures,
  formatInches,
  buildLineItem,
  variantForBand,
  variantForChoices,
  serializeDraft,
  parseDraft,
  commissionRemovals,
  lowestPrice,
  fill,
  formatMoney,
  buildExtraItems,
} = await import(logicUrl || './commission-logic.js');

const STEPS = ['style', 'fabric', 'measure', 'finish'];

/**
 * <commission-configurator>: drives the four commission steps. All content is
 * rendered by sections/commission-configurator.liquid; this only tracks the
 * customer's choices, shows one step at a time and adds the result to the bag.
 * Price = the chosen style's variant for the chosen tweed's band.
 */
class CommissionConfigurator extends HTMLElement {
  /**
   * Phones show the summary and buttons in a bar fixed to the bottom of the
   * screen. Publish its height as --dock-height so the page can leave room for it.
   */
  trackDockHeight() {
    const dock = this.querySelector('[data-dock]');
    if (!dock || !window.ResizeObserver) return;
    const root = document.documentElement;
    new ResizeObserver(() => {
      const fixed = getComputedStyle(dock).position === 'fixed';
      root.style.setProperty('--dock-height', fixed ? `${dock.offsetHeight}px` : '0px');
    }).observe(dock);
  }

  connectedCallback() {
    this.config = JSON.parse(this.querySelector('[data-config]').textContent);
    // Translations arrive HTML-escaped (e.g. &#39;); these strings are set as text.
    Object.keys(this.config.strings).forEach((key) => {
      this.config.strings[key] = decodeEntities(this.config.strings[key]);
    });
    this.garment = this.dataset.garment;
    this.state = { step: 'style', style: null, fabric: null, values: {}, active: 0, finishes: {}, extras: {} };

    this.measures = [...this.querySelectorAll('[data-measure]')].map((row) => ({
      row,
      label: row.dataset.measure,
      min: parseFloat(row.dataset.min),
      max: parseFloat(row.dataset.max),
      diagram: row.dataset.diagram,
      slitOnly: row.dataset.slitOnly === 'true',
      input: row.querySelector('[data-input]'),
    }));
    this.querySelectorAll('[data-finish]').forEach((group) => {
      const chosen = group.querySelector('[aria-pressed="true"]');
      if (chosen) this.chooseFinish(chosen);
    });

    this.bindEvents();
    this.trackDockHeight();
    // Theme editor: selecting a measurement or finishing block opens its step, which
    // would otherwise stay hidden until the earlier steps are done.
    this.addEventListener('shopify:block:select', (event) => this.showBlock(event.target));

    // Going back or refreshing keeps the customer's progress (saved in this browser).
    this.draftKey = `ryin-commission-${this.garment}`;
    this.restoreDraft();

    // A style product page links here with ?style=<handle> to start at the tweed step.
    const preselected = new URLSearchParams(window.location.search).get('style');
    const card = preselected && this.querySelector(`[data-style="${CSS.escape(preselected)}"]:not(:disabled)`);
    if (card && card !== this.state.style?.button) {
      this.selectStyle(card);
      this.state.step = 'fabric';
    }
    this.render();
  }

  /** Restores a saved draft: style, tweed, confirmed measurements, finishing and step. */
  restoreDraft() {
    let draft = null;
    try {
      draft = parseDraft(window.localStorage.getItem(this.draftKey), Date.now(), this.measures.map((m) => m.label));
    } catch {
      return; // Storage blocked (private mode etc.): start fresh.
    }
    if (!draft) return;

    this.state.values = draft.values;
    const card = draft.style && this.querySelector(`[data-style="${CSS.escape(draft.style)}"]:not(:disabled)`);
    if (card) this.selectStyle(card);
    const swatch = draft.fabric && [...this.querySelectorAll('[data-fabric]')].find((s) => s.dataset.name === draft.fabric);
    if (swatch && this.state.style) {
      this.selectFabric(swatch);
      if (!this.bandVariant()) this.state.fabric = null;
    }
    this.querySelectorAll('[data-finish]').forEach((group) => {
      const option = [...group.querySelectorAll('.opt')].find((o) => o.dataset.value === draft.finishes[group.dataset.finish]);
      if (option) this.chooseFinish(option);
    });
    this.state.active = firstUnconfirmed(this.applicableMeasures(), this.state.values);
    // Back to the saved step, or the furthest one its choices still allow.
    const saved = Math.max(0, STEPS.indexOf(draft.step));
    this.state.step = STEPS.slice(0, saved + 1).filter((step) => this.canEnter(step)).pop() || STEPS[0];
  }

  saveDraft() {
    try {
      window.localStorage.setItem(this.draftKey, serializeDraft(this.state, Date.now()));
    } catch {
      // Storage full or blocked: progress just isn't kept.
    }
  }

  selectStyle(card) {
    this.state.style = {
      handle: card.dataset.style,
      name: card.dataset.name,
      title: card.dataset.title,
      slit: card.dataset.slit === 'true',
      variants: JSON.parse(card.dataset.variants),
      button: card,
    };
    this.state.active = firstUnconfirmed(this.applicableMeasures(), this.state.values);
    // A tweed whose band this style doesn't offer can't stay chosen.
    if (this.state.fabric && !this.bandVariant()) this.state.fabric = null;
  }

  selectFabric(swatch) {
    this.state.fabric = {
      name: swatch.dataset.name,
      band: swatch.dataset.band,
      description: swatch.querySelector('template[data-description]'),
      button: swatch,
    };
  }

  /** Records a finishing choice; a paid one (e.g. glass buttons) also records its price variant. */
  chooseFinish(option) {
    const title = option.closest('[data-finish]').dataset.finish;
    this.state.finishes[title] = option.dataset.value;
    this.state.extras[title] = option.dataset.extraId
      ? { id: option.dataset.extraId, cents: Number(option.dataset.extraCents), price: option.dataset.extraPrice }
      : null;
  }

  /** The piece's price with paid finishing choices, formatted, or null before a tweed is chosen. */
  totalPrice() {
    const variant = this.variant();
    if (!variant) return null;
    const extras = Object.values(this.state.extras).filter(Boolean);
    if (!extras.length) return variant.price;
    const cents = extras.reduce((sum, extra) => sum + extra.cents, variant.cents);
    return formatMoney(cents, this.config.moneyFormat);
  }

  /** Finishing choices that are product options (e.g. Buttons), as { option name: value }. */
  optionPicks() {
    const picks = {};
    this.querySelectorAll('[data-option]').forEach((group) => {
      const value = this.state.finishes[group.dataset.finish];
      if (value !== undefined) picks[group.dataset.option] = value;
    });
    return picks;
  }

  /** The cheapest variant for the chosen style and tweed band (gates the steps), or null. */
  bandVariant() {
    const { style, fabric } = this.state;
    if (!style || !fabric) return null;
    return variantForBand(style.variants, fabric.band);
  }

  /** The variant for the chosen style, tweed band and option choices (e.g. glass buttons), or null. */
  variant() {
    const { style, fabric } = this.state;
    if (!style || !fabric) return null;
    return variantForChoices(style.variants, fabric.band, this.optionPicks());
  }

  /**
   * Option-driven finishing (e.g. Buttons: Fabric / Glass): each choice shows what
   * it adds over the cheapest ("+$50") or "Included"; choices the style doesn't
   * offer are disabled, and a disabled choice falls back to the first one left.
   */
  renderOptionChoices() {
    const { style, fabric } = this.state;
    const base = this.bandVariant();
    if (!style || !fabric || !base) return;
    const strings = this.config.strings;
    this.querySelectorAll('[data-option]').forEach((group) => {
      const picks = this.optionPicks();
      let firstEnabled = null;
      group.querySelectorAll('.opt').forEach((option) => {
        const match = variantForChoices(style.variants, fabric.band, { ...picks, [group.dataset.option]: option.dataset.value });
        option.disabled = !match;
        if (match && !firstEnabled) firstEnabled = option;
        const extra = match ? match.cents - base.cents : 0;
        option.querySelector('[data-opt-price]').textContent = !match
          ? strings.unavailable
          : extra > 0
            ? fill(strings.extraPrice, { price: formatMoney(extra, this.config.moneyFormat) })
            : strings.included;
      });
      const chosen = [...group.querySelectorAll('.opt')].find((o) => o.dataset.value === this.state.finishes[group.dataset.finish]);
      if ((!chosen || chosen.disabled) && firstEnabled) this.chooseFinish(firstEnabled);
    });
  }

  bindEvents() {
    this.addEventListener('click', (event) => {
      const target = event.target.closest('button');
      if (!target || !this.contains(target)) return;

      if (target.matches('[data-style]')) {
        this.selectStyle(target);
      } else if (target.matches('[data-fabric]')) {
        this.selectFabric(target);
      } else if (target.matches('.opt')) {
        this.chooseFinish(target);
      } else if (target.matches('[data-stage]')) {
        this.goTo(target.dataset.stage);
        return;
      } else if (target.matches('[data-next]')) {
        this.goTo(target.dataset.next);
        return;
      } else if (target.matches('[data-back]')) {
        this.goTo(STEPS[STEPS.indexOf(this.state.step) - 1]);
        return;
      } else if (target.matches('[data-edit]')) {
        this.state.active = this.applicableMeasures().findIndex((m) => m.row === target.closest('[data-measure]'));
        this.render();
        this.currentMeasure()?.input.focus();
        return;
      } else if (target.matches('[data-clear-measures]')) {
        this.clearMeasures();
        return;
      } else if (target.matches('[data-confirm]')) {
        this.confirmMeasure();
        return;
      } else if (target.matches('[data-add]')) {
        this.addToBag(target);
        return;
      } else {
        return;
      }
      this.render();
    });

    this.measures.forEach((measure) => {
      measure.input.addEventListener('input', () => this.updateMeasureInput(measure));
      measure.input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.confirmMeasure();
        }
      });
    });
  }

  applicableMeasures() {
    return activeMeasures(this.measures, Boolean(this.state.style?.slit));
  }

  currentMeasure() {
    return this.applicableMeasures()[this.state.active];
  }

  confirmedValues() {
    const byDiagram = {};
    this.applicableMeasures().forEach((m) => {
      if (this.state.values[m.label] !== undefined) byDiagram[m.diagram] = this.state.values[m.label];
    });
    return byDiagram;
  }

  updateMeasureInput(measure) {
    const value = parseInches(measure.input.value, { allowZero: measure.slitOnly });
    measure.row.querySelector('[data-confirm]').disabled = value === null;
    const warning = measurementWarning(measure, value, this.confirmedValues(), this.config.strings);
    const warningEl = measure.row.querySelector('[data-warning]');
    warningEl.textContent = warning;
    warningEl.hidden = !warning;
  }

  /** "Clear measurements and start again": after a confirm, empties them and returns to the first. */
  clearMeasures() {
    if (!window.confirm(this.config.strings.clearConfirm)) return;
    this.state.values = {};
    this.state.active = 0;
    this.measures.forEach((measure) => {
      measure.input.value = '';
      this.updateMeasureInput(measure);
    });
    this.render();
    this.currentMeasure()?.input.focus();
  }

  confirmMeasure() {
    const measure = this.currentMeasure();
    if (!measure) return;
    const value = parseInches(measure.input.value, { allowZero: measure.slitOnly });
    if (value === null) {
      measure.input.focus();
      return;
    }
    this.state.values[measure.label] = value;
    this.state.active = firstUnconfirmed(this.applicableMeasures(), this.state.values);
    this.render();
    const next = this.currentMeasure();
    if (next) next.input.focus();
    else this.querySelector('[data-next="finish"]').focus();
  }

  goTo(step) {
    if (!this.canEnter(step)) return;
    this.state.step = step;
    if (step === 'measure') this.state.active = firstUnconfirmed(this.applicableMeasures(), this.state.values);
    this.render();

    const heading = this.querySelector(`[data-step="${step}"] .step__heading`);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.closest('.commission').scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth' });
    heading.focus({ preventScroll: true });
    if (step === 'measure') this.currentMeasure()?.input.focus({ preventScroll: true });
  }

  /** Theme editor only: shows the step holding a block, skipping the step gating. */
  showBlock(block) {
    const step = block.closest('[data-step]')?.dataset.step;
    if (!step) return;
    this.state.step = step;
    const index = this.applicableMeasures().findIndex((m) => m.row === block);
    if (index !== -1) this.state.active = index;
    this.render();
  }

  /** Whether every step before `step` is complete. */
  canEnter(step) {
    const index = STEPS.indexOf(step);
    if (index >= 1 && !this.state.style) return false;
    if (index >= 2 && !this.bandVariant()) return false;
    if (index >= 3 && !this.measuresComplete()) return false;
    return index !== -1;
  }

  measuresComplete() {
    const measures = this.applicableMeasures();
    return measures.every((m) => this.state.values[m.label] !== undefined) && this.state.active >= measures.length;
  }

  render() {
    const { step, style, fabric } = this.state;
    const stepIndex = STEPS.indexOf(step);

    this.querySelectorAll('[data-step]').forEach((panel) => {
      panel.hidden = panel.dataset.step !== step;
    });
    this.querySelectorAll('[data-stage]').forEach((tab) => {
      const index = STEPS.indexOf(tab.dataset.stage);
      tab.classList.toggle('is-current', index === stepIndex);
      tab.classList.toggle('is-done', index < stepIndex);
      tab.disabled = index >= stepIndex;
      if (index === stepIndex) tab.setAttribute('aria-current', 'step');
      else tab.removeAttribute('aria-current');
    });

    this.querySelectorAll('[data-style]').forEach((b) => b.setAttribute('aria-pressed', b === style?.button));
    this.renderSwatches();
    this.renderOptionChoices();
    this.querySelectorAll('[data-finish]').forEach((group) => {
      group.querySelectorAll('.opt').forEach((b) => {
        b.setAttribute('aria-pressed', this.state.finishes[group.dataset.finish] === b.dataset.value);
      });
    });

    this.renderMeasures();
    this.renderActions(step);
    this.renderSummary();
    this.saveDraft();
  }

  /** Each tweed's price for the chosen style; tweeds the style can't be made in are disabled. */
  renderSwatches() {
    const { style, fabric } = this.state;
    this.querySelectorAll('[data-fabric]').forEach((swatch) => {
      swatch.setAttribute('aria-pressed', swatch === fabric?.button);
      const band = swatch.dataset.band;
      const variant = style ? variantForBand(style.variants, band) : null;
      swatch.disabled = Boolean(style) && !variant;
      swatch.querySelector('[data-swatch-unavailable]').hidden = !swatch.disabled;
      swatch.querySelector('[data-swatch-price]').textContent = variant
        ? fill(this.config.strings.bandPrice, { band, price: variant.price })
        : band;
    });

    // Two copies: under the swatches (desktop) and atop the phone bar (tweed step only).
    const description = fabric?.description;
    this.querySelectorAll('[data-fabric-note]').forEach((note) => {
      note.hidden = !description || ('dockNote' in note.dataset && this.state.step !== 'fabric');
      if (description && note.dataset.for !== fabric.name) {
        note.dataset.for = fabric.name;
        note.querySelector('[data-fabric-note-name]').textContent = fabric.name;
        note.querySelector('[data-fabric-note-text]').replaceChildren(description.content.cloneNode(true));
        window.preventOrphans?.(note);
      }
    });
  }

  /** Lowest available price for the chosen style, else for the whole collection. */
  fromPrice() {
    return (this.state.style && lowestPrice(this.state.style.variants)) || this.config.fromPrice;
  }

  renderMeasures() {
    const applicable = this.applicableMeasures();
    this.querySelector('[data-clear-measures]').hidden = !applicable.some((m) => this.state.values[m.label] !== undefined);
    this.measures.forEach((measure) => {
      const index = applicable.indexOf(measure);
      measure.row.hidden = index === -1;
      if (index === -1) return;

      const value = this.state.values[measure.label];
      const isActive = index === this.state.active;
      const isDone = value !== undefined && !isActive;
      measure.row.classList.toggle('is-active', isActive);
      measure.row.classList.toggle('is-done', isDone);
      measure.row.classList.toggle('is-locked', !isActive && !isDone);
      measure.row.querySelector('[data-num]').textContent = isDone ? '✓' : index + 1;
      measure.row.querySelector('[data-value]').textContent = value !== undefined ? formatInches(value) : '';

      if (isActive && document.activeElement !== measure.input) {
        measure.input.value = value !== undefined ? String(value) : '';
        this.updateMeasureInput(measure);
      }
    });
  }

  renderActions(step) {
    this.querySelector('[data-back]').hidden = step === STEPS[0];
    const next = { style: 'fabric', fabric: 'measure', measure: 'finish' }[step];
    this.querySelectorAll('[data-next]').forEach((button) => {
      button.hidden = button.dataset.next !== next;
      button.disabled = !this.canEnter(button.dataset.next);
    });
    const add = this.querySelector('[data-add]');
    add.hidden = step !== 'finish';
    this.querySelector('[data-add-price]').textContent = this.totalPrice() || this.fromPrice();
  }

  renderSummary() {
    const { style, fabric, step } = this.state;
    const strings = this.config.strings;
    const measures = this.applicableMeasures();
    const measured = measures.filter((m) => this.state.values[m.label] !== undefined).length;

    if (style) this.querySelector('[data-summary-title]').textContent = style.title;
    this.querySelector('[data-row="style"]').textContent = style ? style.name : '—';
    this.querySelector('[data-row="fabric"]').textContent = fabric
      ? [fabric.name, fabric.band].filter(Boolean).join(' · ')
      : '—';
    this.querySelector('[data-row="measure"]').textContent = fill(strings.measured, {
      done: measured,
      total: measures.length,
    });

    const finishRows = this.querySelector('[data-finish-rows]');
    finishRows.hidden = step !== 'finish';
    finishRows.replaceChildren(
      ...Object.entries(this.state.finishes).flatMap(([title, choice]) => {
        const dt = document.createElement('dt');
        const dd = document.createElement('dd');
        const extra = this.state.extras[title];
        dt.textContent = title;
        dd.textContent = extra ? fill(strings.extraChoice, { choice, price: extra.price }) : choice;
        return [dt, dd];
      })
    );

    const variant = this.variant();
    this.querySelector('[data-summary-total-label]').textContent = variant ? strings.total : strings.from;
    this.querySelector('[data-summary-total]').textContent = this.totalPrice() || this.fromPrice();

    this.renderPreview();
  }

  /**
   * Until a tweed is chosen, the chosen style's drawing. Then the tweed drawn
   * onto that drawing (<garment-preview> in theme.js, owner 2026-11-02, with a
   * "preview only" disclaimer); a plain square of the cloth when the style has
   * no usable drawing.
   */
  renderPreview() {
    const { style, fabric } = this.state;
    const preview = this.querySelector('[data-summary-preview]');
    const key = fabric ? `fabric:${fabric.name}:${style?.name || ''}` : `style:${style?.name || ''}`;
    if (preview.dataset.key === key) return;
    preview.dataset.key = key;

    const surface = fabric?.button.querySelector('.fabric-surface');
    const drawing = style?.button.dataset.drawing;
    const texture = fabric?.button.dataset.texture;
    const drawn = Boolean(surface && drawing && texture);
    preview.classList.toggle('summary__preview--fabric', Boolean(surface) && !drawn);
    this.querySelectorAll('[data-preview-disclaimer]').forEach((note) => {
      note.hidden = !drawn;
    });
    if (surface) {
      const cloth = surface.cloneNode(true);
      cloth.classList.remove('swatch__chip');
      cloth.classList.add('summary__fabric');
      // The swatch asks for a 200px image; the summary shows it larger.
      cloth.querySelector('img')?.setAttribute('sizes', '400px');
      if (drawn) {
        // The plain square stays inside as the fallback if the drawing can't be used.
        const garment = document.createElement('garment-preview');
        garment.append(cloth);
        garment.dataset.drawing = drawing;
        garment.dataset.tweed = texture;
        preview.replaceChildren(garment);
      } else {
        preview.replaceChildren(cloth);
      }
    } else if (style) {
      const art = style.button.querySelector('img, svg');
      preview.replaceChildren(art ? art.cloneNode(true) : '');
    } else {
      preview.replaceChildren();
    }
  }

  async addToBag(button) {
    const error = this.querySelector('[data-error]');
    error.hidden = true;
    if (button.hasAttribute('data-preview-mode')) {
      error.textContent = this.config.strings.previewOnly;
      error.hidden = false;
      return;
    }

    const measures = this.applicableMeasures();
    const item = buildLineItem({
      variantId: this.variant().id,
      garment: this.garment,
      tweed: this.state.fabric.name,
      measures,
      values: this.state.values,
      finishes: this.state.finishes,
      labels: { tweed: this.config.strings.tweed },
    });

    button.disabled = true;
    try {
      // One commission per order: a new design replaces any earlier one. Add
      // first, so a failed add leaves the earlier design in the bag.
      const cart = await fetchJson(`${this.config.cartUrl}.js`);
      const extras = buildExtraItems({
        extras: this.state.extras,
        garment: this.garment,
        forLabel: this.config.strings.addonFor,
        pieceTitle: this.state.style.title,
      });
      const added = await postJson(`${this.config.cartUrl}/add.js`, { items: [item, ...extras] });
      const updates = commissionRemovals(cart.items, (added.items || []).map((line) => line.key));
      if (Object.keys(updates).length) await postJson(`${this.config.cartUrl}/update.js`, { updates });
      window.location.href = this.config.cartUrl;
    } catch (err) {
      // Show Shopify's own message (e.g. "sold out"), not the browser's "Failed to fetch".
      error.textContent = (err instanceof CartError && err.message) || this.config.strings.addError;
      error.hidden = false;
      button.disabled = false;
    }
  }
}

function decodeEntities(text) {
  return new DOMParser().parseFromString(text, 'text/html').documentElement.textContent;
}

/** An error response from the cart API; its message is Shopify's, shown to the customer. */
class CartError extends Error {}

async function fetchJson(url) {
  const response = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new CartError();
  return response.json();
}

async function postJson(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new CartError(data.description || data.message || '');
  return data;
}

customElements.define('commission-configurator', CommissionConfigurator);
